import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCITranslate } from '../../i18n/customerIdentity';
import {
  CIConfirmPopup,
  CIDrawer,
  CIInlineAlert,
} from './CustomerIdentityShared';
import CIEditHistory from './CIEditHistory';
import EntitySettingsPanel from './EntitySettingsPanel';
import {
  adminResetCustomerUser2fa,
  adminSetCustomerUserPassword,
  adminUpdateCustomerUser2faChannels,
  createCustomerUser,
  deleteCustomerUser,
  getCustomerUserHistory,
  listCustomerUsers,
  listUserGroups,
  setCustomerUserGroupMemberships,
  updateCustomerUser,
} from '../../services/customerIdentityApi';

const EMPTY_FORM = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  phone: '',
  hire_date: '',
  role: 'member',
  external_user_id: '',
};

/**
 * Builds a `{ kind, error }` payload from a thrown `CIError`. The shared
 * alert component knows how to translate it via the i18n module.
 */
const errorMessage = (err) => ({ kind: 'danger', error: err });

const successMessage = (i18nKey, params) => ({
  kind: 'success',
  i18nKey,
  i18nParams: params,
});

export default function CustomerUsersTab({ customer, setMessage }) {
  const t = useCITranslate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [resetUserId, setResetUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  // Errors raised inside the drawer/popup live with the form, not the page.
  // Success bubbles up to the page-level info bar via `setMessage`.
  const [drawerMessage, setDrawerMessage] = useState(null);
  const [resetMessage, setResetMessage] = useState(null);
  const [busy2fa, setBusy2fa] = useState(false);
  const [pendingReset2fa, setPendingReset2fa] = useState(null);
  const [siteGroups, setSiteGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const settingsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCustomerUsers(customer.id, true);
      setUsers(data || []);
    } catch (err) {
      setMessage({
        kind: 'danger',
        i18nKey: 'users.couldNotLoad',
        // Keep the original error around so devs can inspect via console.
        error: err,
      });
    } finally {
      setLoading(false);
    }
  }, [customer.id, setMessage]);

  const loadGroups = useCallback(async () => {
    if (!customer.site_id) return;
    try {
      const data = await listUserGroups(customer.site_id);
      setSiteGroups(data || []);
    } catch (_err) {
      setSiteGroups([]);
    }
  }, [customer.site_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    if (editing) {
      setForm({
        ...EMPTY_FORM,
        ...editing,
        password: '',
        hire_date: editing.hire_date ? String(editing.hire_date).slice(0, 10) : '',
      });
      setSelectedGroupIds((editing.user_group_ids || []).map(String));
    } else if (creating) {
      setForm(EMPTY_FORM);
      setSelectedGroupIds([]);
    } else {
      setForm(EMPTY_FORM);
      setSelectedGroupIds([]);
    }
    setDrawerMessage(null);
  }, [editing, creating]);

  useEffect(() => {
    if (!resetUserId) {
      setNewPassword('');
      setResetMessage(null);
    }
  }, [resetUserId]);

  const setField = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
    if (drawerMessage?.kind === 'danger') setDrawerMessage(null);
  };

  const closeDrawer = () => {
    setCreating(false);
    setEditing(null);
    setDrawerMessage(null);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setDrawerMessage(null);
    try {
      let userId;
      const hirePayload = form.hire_date?.trim() ? form.hire_date.trim() : null;
      if (editing) {
        await updateCustomerUser(editing.id, {
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          hire_date: hirePayload,
          role: form.role,
          external_user_id: form.external_user_id || null,
        });
        userId = editing.id;
      } else {
        const created = await createCustomerUser({
          customer_id: customer.id,
          email: form.email,
          password: form.password || null,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          hire_date: hirePayload,
          role: form.role,
          external_user_id: form.external_user_id || null,
        });
        userId = created.id;
      }
      await setCustomerUserGroupMemberships(userId, selectedGroupIds);
      try {
        await settingsRef.current?.flush(userId);
      } catch (settingsErr) {
        setDrawerMessage(errorMessage(settingsErr));
        await load();
        return;
      }
      closeDrawer();
      await load();
      await loadGroups();
      setMessage(successMessage('users.saved'));
    } catch (err) {
      // Stay open and show the error inside the drawer so the admin can
      // correct the input without losing typed values.
      setDrawerMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteCustomerUser(pendingDelete.id);
      setPendingDelete(null);
      await load();
      setMessage(successMessage('users.removed'));
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetUserId || !newPassword) return;
    setBusy(true);
    setResetMessage(null);
    try {
      await adminSetCustomerUserPassword(resetUserId, newPassword);
      setResetUserId(null);
      setNewPassword('');
      setMessage(successMessage('users.passwordUpdated'));
    } catch (err) {
      setResetMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const twoFaSummaryLine = (u) => {
    const parts = [];
    if (u.totp_verified) parts.push(t('users.twoFa.app'));
    if (u.email_otp_enabled) parts.push(t('users.twoFa.emailChannel'));
    if (u.sms_otp_enabled) parts.push(t('users.twoFa.smsChannel'));
    if (parts.length) return parts.join(' · ');
    // `is_2fa_enabled` can be true while channel flags are false (stuck/partial
    // enrollment). Show a clear label so it doesn’t look like “None” + “Enabled”.
    if (u.is_2fa_enabled) return t('users.twoFa.summaryOrphan');
    return t('users.twoFa.summaryNone');
  };

  /** True if any 2FA signal is on — used for Reset eligibility and badges. */
  const has2faSignal = (u) =>
    !!(
      u?.is_2fa_enabled ||
      u?.totp_verified ||
      u?.email_otp_enabled ||
      u?.sms_otp_enabled
    );

  const formatUserGroupLabels = (u) => {
    const ids = (u.user_group_ids || []).map(String);
    if (!ids.length) return t('users.groupsNone');
    const labels = ids
      .map((id) => siteGroups.find((g) => String(g.id) === id)?.name)
      .filter(Boolean);
    return labels.length ? labels.join(', ') : t('users.groupsNone');
  };

  const toggleGroupMembership = (groupId) => {
    const s = String(groupId);
    setSelectedGroupIds((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
    if (drawerMessage?.kind === 'danger') setDrawerMessage(null);
  };

  const mergeUpdatedUser = (updated) => {
    if (!updated?.id) return;
    setUsers((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    setEditing((prev) => (prev && prev.id === updated.id ? updated : prev));
  };

  const handleToggleEmailOtp = async (enabled) => {
    if (!editing) return;
    setBusy2fa(true);
    setDrawerMessage(null);
    try {
      const updated = await adminUpdateCustomerUser2faChannels(editing.id, {
        email_otp_enabled: enabled,
      });
      mergeUpdatedUser(updated);
      setMessage(successMessage('users.twoFa.channelsUpdated'));
    } catch (err) {
      setDrawerMessage(errorMessage(err));
    } finally {
      setBusy2fa(false);
    }
  };

  const handleToggleSmsOtp = async (enabled) => {
    if (!editing) return;
    setBusy2fa(true);
    setDrawerMessage(null);
    try {
      const updated = await adminUpdateCustomerUser2faChannels(editing.id, {
        sms_otp_enabled: enabled,
      });
      mergeUpdatedUser(updated);
      setMessage(successMessage('users.twoFa.channelsUpdated'));
    } catch (err) {
      setDrawerMessage(errorMessage(err));
    } finally {
      setBusy2fa(false);
    }
  };

  const handleConfirmReset2fa = async () => {
    if (!pendingReset2fa) return;
    setBusy2fa(true);
    setDrawerMessage(null);
    try {
      const updated = await adminResetCustomerUser2fa(pendingReset2fa.id);
      setPendingReset2fa(null);
      mergeUpdatedUser(updated);
      setMessage(successMessage('users.twoFa.resetDone'));
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy2fa(false);
    }
  };

  const isIndividual = customer.account_type === 'individual';
  const drawerTitle = editing ? t('users.editUser') : t('users.addUser');
  const pendingDeleteEmail = pendingDelete?.email || '';
  const pendingReset2faEmail = pendingReset2fa?.email || '';

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="m-0">{t('users.title')}</h6>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => setCreating(true)}
          disabled={isIndividual && users.length >= 1}
        >
          <i className="bi bi-plus-lg me-1" />
          {t('users.addUser')}
        </button>
      </div>
      {isIndividual ? (
        <div className="card-body py-2 text-muted small">
          {t('users.individualNotice')}
        </div>
      ) : null}
      <div className="table-responsive">
        <table className="table m-0">
          <thead>
            <tr>
              <th>{t('users.columns.email')}</th>
              <th>{t('users.columns.name')}</th>
              <th className="text-muted small">{t('users.fields.externalUserId')}</th>
              <th>{t('users.columns.hireDate')}</th>
              <th>{t('users.columns.role')}</th>
              <th>{t('users.columns.groups')}</th>
              <th>{t('users.columns.twoFa')}</th>
              <th>{t('users.columns.status')}</th>
              <th className="text-end">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-muted py-3">
                  {t('common.loading')}
                </td>
              </tr>
            ) : null}
            {users.length === 0 && !loading ? (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">
                  {t('users.empty')}
                </td>
              </tr>
            ) : null}
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.email}</td>
                <td>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}</td>
                <td className="small text-muted">{u.external_user_id || '—'}</td>
                <td className="small text-muted text-nowrap">
                  {u.hire_date
                    ? String(u.hire_date).slice(0, 10)
                    : '—'}
                </td>
                <td>
                  <span
                    className={`badge ${u.role === 'owner' ? 'bg-primary' : u.role === 'admin' ? 'bg-info' : 'bg-secondary'}`}
                  >
                    {t(`users.roles.${u.role}`) || u.role}
                  </span>
                </td>
                <td className="small text-muted">{formatUserGroupLabels(u)}</td>
                <td>
                  <div className="d-flex flex-column align-items-start gap-1">
                    <span
                      className={`small ${u.is_2fa_enabled && !u.totp_verified && !u.email_otp_enabled && !u.sms_otp_enabled ? 'text-warning' : 'text-muted'}`}
                      title={
                        u.is_2fa_enabled &&
                        !u.totp_verified &&
                        !u.email_otp_enabled &&
                        !u.sms_otp_enabled
                          ? t('users.twoFa.summaryOrphanHint')
                          : undefined
                      }
                    >
                      {twoFaSummaryLine(u)}
                    </span>
                    {has2faSignal(u) ? (
                      <span className="badge bg-success">{t('users.twoFa.enabled')}</span>
                    ) : (
                      <span className="badge bg-light text-dark">{t('users.twoFa.off')}</span>
                    )}
                  </div>
                </td>
                <td>
                  <span
                    className={`badge ${u.status === 'active' ? 'bg-success' : 'bg-secondary'}`}
                  >
                    {t(`users.statuses.${u.status}`) || u.status}
                  </span>
                </td>
                <td className="text-end">
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setEditing(u)}
                    >
                      {t('users.actions.edit')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-warning"
                      onClick={() => setResetUserId(u.id)}
                    >
                      {t('users.actions.resetPassword')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => setPendingDelete(u)}
                    >
                      {t('users.actions.delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CIDrawer
        open={creating || Boolean(editing)}
        title={drawerTitle}
        onClose={closeDrawer}
      >
        <form onSubmit={submitForm} noValidate>
          {/* Errors and warnings from the save call render here, inside the
              drawer, so the admin can keep their typed values. */}
          <CIInlineAlert
            message={drawerMessage}
            onDismiss={() => setDrawerMessage(null)}
          />

          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">{t('users.fields.email')}</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={setField('email')}
                required
                disabled={Boolean(editing)}
              />
            </div>
            {!editing ? (
              <div className="col-12 col-md-6">
                <label className="form-label">
                  {t('users.fields.initialPassword')}
                </label>
                <input
                  type="password"
                  className="form-control"
                  value={form.password}
                  onChange={setField('password')}
                  placeholder={t('users.fields.initialPasswordHint')}
                />
              </div>
            ) : null}
            <div className="col-12 col-md-6">
              <label className="form-label">{t('users.fields.firstName')}</label>
              <input
                className="form-control"
                value={form.first_name || ''}
                onChange={setField('first_name')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('users.fields.lastName')}</label>
              <input
                className="form-control"
                value={form.last_name || ''}
                onChange={setField('last_name')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('users.fields.phone')}</label>
              <input
                className="form-control"
                value={form.phone || ''}
                onChange={setField('phone')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('users.fields.hireDate')}</label>
              <input
                type="date"
                className="form-control"
                value={form.hire_date || ''}
                onChange={setField('hire_date')}
                aria-label={t('users.fields.hireDate')}
              />
              <p className="form-text small mb-0">{t('users.fields.hireDateHint')}</p>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('users.fields.role')}</label>
              <select
                className="form-select"
                value={form.role}
                onChange={setField('role')}
              >
                <option value="owner">{t('users.roles.owner')}</option>
                {!isIndividual ? (
                  <option value="admin">{t('users.roles.admin')}</option>
                ) : null}
                {!isIndividual ? (
                  <option value="member">{t('users.roles.member')}</option>
                ) : null}
              </select>
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('users.fields.externalUserId')}</label>
              {editing ? (
                <p className="form-control-plaintext text-muted small mb-0">
                  {form.external_user_id || '—'}
                </p>
              ) : (
                <input
                  className="form-control"
                  value={form.external_user_id || ''}
                  onChange={setField('external_user_id')}
                />
              )}
            </div>
            <div className="col-12">
              <label className="form-label">{t('users.columns.groups')}</label>
              <p className="small text-muted mb-2">{t('users.groupsHint')}</p>
              {siteGroups.length === 0 ? (
                <p className="small text-warning mb-0">{t('users.groupsManageOnSite')}</p>
              ) : (
                <div
                  className="border rounded p-2 bg-light"
                  style={{ maxHeight: 180, overflowY: 'auto' }}
                >
                  {siteGroups.map((g) => (
                    <div key={g.id} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`ci-ug-${g.id}`}
                        checked={selectedGroupIds.includes(String(g.id))}
                        onChange={() => toggleGroupMembership(g.id)}
                      />
                      <label className="form-check-label" htmlFor={`ci-ug-${g.id}`}>
                        {g.name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {editing ? (
              <div className="col-12">
                <hr className="my-3" />
                <h6 className="mb-2">{t('users.twoFa.sectionTitle')}</h6>
                <p className="small text-muted mb-3">{t('users.twoFa.sectionHint')}</p>
                <dl className="row small mb-3">
                  <dt className="col-sm-4 text-muted">{t('users.twoFa.app')}</dt>
                  <dd className="col-sm-8 mb-2">
                    {editing.totp_verified ? (
                      <span className="badge bg-success">{t('users.twoFa.on')}</span>
                    ) : (
                      <span className="badge bg-secondary">{t('users.twoFa.off')}</span>
                    )}
                  </dd>
                </dl>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="ci-user-email-otp"
                    checked={!!editing.email_otp_enabled}
                    onChange={(e) => handleToggleEmailOtp(e.target.checked)}
                    disabled={busy2fa}
                  />
                  <label className="form-check-label" htmlFor="ci-user-email-otp">
                    {t('users.twoFa.emailChannel')}
                  </label>
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="ci-user-sms-otp"
                    checked={!!editing.sms_otp_enabled}
                    onChange={(e) => handleToggleSmsOtp(e.target.checked)}
                    disabled={busy2fa}
                  />
                  <label className="form-check-label" htmlFor="ci-user-sms-otp">
                    {t('users.twoFa.smsChannel')}
                  </label>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => setPendingReset2fa(editing)}
                  disabled={busy2fa || !has2faSignal(editing)}
                >
                  {t('users.twoFa.resetAll')}
                </button>
              </div>
            ) : null}
          </div>

          <EntitySettingsPanel
            ref={settingsRef}
            scope="user"
            scopeId={editing?.id || null}
            contextSiteId={customer.site_id}
            contextCustomerId={customer.id}
            contextUserId={editing?.id || null}
            defaultExpanded={Boolean(editing)}
            onError={(err) => setDrawerMessage(errorMessage(err))}
          />

          {editing ? (
            <CIEditHistory
              fetchHistory={() => getCustomerUserHistory(editing.id)}
              updatedAt={editing.updated_at}
              resourceId={editing.id}
            />
          ) : null}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="button" className="btn btn-secondary" onClick={closeDrawer}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t('common.saving') : t('users.saveUser')}
            </button>
          </div>
        </form>
      </CIDrawer>

      <CIConfirmPopup
        open={Boolean(pendingDelete)}
        title={t('users.deleteConfirmTitle')}
        message={t('users.deleteConfirm', { email: pendingDeleteEmail })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <CIConfirmPopup
        open={Boolean(pendingReset2fa)}
        title={t('users.twoFa.resetConfirmTitle')}
        message={t('users.twoFa.resetConfirm', { email: pendingReset2faEmail })}
        confirmLabel={t('users.twoFa.resetAll')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={handleConfirmReset2fa}
        onCancel={() => setPendingReset2fa(null)}
      />

      <CIConfirmPopup
        open={Boolean(resetUserId)}
        title={t('users.resetPasswordTitle')}
        message={
          <div>
            <p>{t('users.resetPasswordIntro')}</p>
            <input
              type="password"
              className="form-control"
              placeholder={t('users.newPasswordPlaceholder')}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <CIInlineAlert
              message={resetMessage}
              onDismiss={() => setResetMessage(null)}
              className="mt-3 mb-0"
            />
          </div>
        }
        confirmLabel={t('users.setPassword')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleResetPassword}
        onCancel={() => {
          setResetUserId(null);
          setNewPassword('');
          setResetMessage(null);
        }}
      />
    </div>
  );
}
